import type { Container } from "pixi.js";

import type {
  IOverlayElementAnimation,
  IOverlayElementKeyframes,
  IOverlayKeyframeStop,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

/**
 * Per-frame animation for overlay elements in the Pixi lane (scene mask + áreas).
 * El DOM anima vía CSS keyframes (`OverlayElementAnimations.styles.ts`); acá
 * replicamos las mismas curvas mutando un `animContainer` que envuelve el wrapper
 * del elemento. El container tiene pivot = centro del elemento, así scale-in/pulse
 * escalan desde el centro (como el `transform-origin` por defecto del DOM) y los
 * slide-in se hacen con un offset de posición sobre el pivot.
 *
 * Sin app.ticker propio: lo maneja el ticker de RenderFactory (un solo callback
 * que itera los handles), removido en `clear()` para no leakear / degradar fps.
 */
export interface OverlayAnimHandle {
  /** Container envoltorio con pivot=centro del elemento (en coords de diseño). */
  container: Container;
  animation: IOverlayElementAnimation;
  /** Bounds del elemento en px de diseño (para los offsets de slide-in). */
  w: number;
  h: number;
}

/**
 * Updater per-frame genérico (animación de elementos, partículas, procedural,
 * typewriter, shaders animados). El ticker de RenderFactory lo llama con el
 * tiempo transcurrido (ms) desde que se montó la composición.
 */
/**
 * Updater per-frame. `elapsedMs` es el tiempo desde que arrancó el reloj de la
 * capa; `clockId` identifica ese reloj (cambia cuando la capa se vuelve a
 * mostrar): un updater con estado propio (typewriter, partículas) lo usa para
 * reiniciarse. Devolver `true` significa "terminé" (animación finita en su
 * estado final): el ticker deja de llamarlo hasta el próximo reloj.
 */
export type OverlayFrameUpdater = (elapsedMs: number, clockId?: number) => void | boolean;

/**
 * Lista VIVA de updaters: algunos se registran tarde (el typewriter espera a
 * que cargue la fuente), por eso las factories conservan la misma instancia y
 * `onPush` avisa al ticker para que vuelva a registrarse si hacía falta.
 */
export class UpdaterList extends Array<OverlayFrameUpdater> {
  onPush?: () => void;

  override push(...items: OverlayFrameUpdater[]): number {
    const n = super.push(...items);
    this.onPush?.();
    return n;
  }
}

type Easing = NonNullable<IOverlayElementAnimation["easing"]>;

const ease = (kind: Easing, t: number): number => {
  switch (kind) {
    case "linear":
      return t;
    case "ease-in":
      return t * t;
    case "ease-out":
      return 1 - (1 - t) * (1 - t);
    case "ease-in-out":
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    case "ease":
    default:
      return t * t * (3 - 2 * t); // smoothstep ≈ CSS `ease`
  }
};

/**
 * Curvas cubic-bezier estándar de CSS (animation-timing-function). Se usan
 * SOLO para keyframes, donde el DOM anima con estas mismas curvas; las
 * entry-anims/pulse conservan `ease()` para no cambiar su look.
 */
const EASE_BEZIER = [0.25, 0.1, 0.25, 1] as const;
const CSS_BEZIER: Record<
  Easing,
  readonly [number, number, number, number] | "linear"
> = {
  linear: "linear",
  ease: EASE_BEZIER,
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
};

/** Un eje de la Bézier cúbica con P0=0, P3=1 y controles p1/p2. */
const bezierAxis = (p1: number, p2: number, u: number): number => {
  const v = 1 - u;
  return 3 * v * v * u * p1 + 3 * v * u * u * p2 + u * u * u;
};

const bezierAxisDeriv = (p1: number, p2: number, u: number): number => {
  const v = 1 - u;
  return 3 * v * v * p1 + 6 * v * u * (p2 - p1) + 3 * u * u * (1 - p2);
};

const BEZIER_EPS = 1e-6;

/**
 * Evalúa `cubic-bezier(x1,y1,x2,y2)` en `t` (0..1) como CSS: resuelve el
 * parámetro u con Bx(u)=t (Newton, bisección de respaldo) y devuelve By(u).
 * Easing desconocido → `ease`.
 */
export const cssEase = (kind: Easing, t: number): number => {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const curve = CSS_BEZIER[kind] ?? EASE_BEZIER;
  if (curve === "linear") return t;
  const [x1, y1, x2, y2] = curve;
  let u = t;
  for (let i = 0; i < 8; i += 1) {
    const err = bezierAxis(x1, x2, u) - t;
    if (Math.abs(err) < BEZIER_EPS) return bezierAxis(y1, y2, u);
    const d = bezierAxisDeriv(x1, x2, u);
    if (Math.abs(d) < BEZIER_EPS) break;
    u = Math.min(1, Math.max(0, u - err / d));
  }
  let lo = 0;
  let hi = 1;
  while (hi - lo > BEZIER_EPS) {
    u = (lo + hi) / 2;
    const x = bezierAxis(x1, x2, u);
    if (Math.abs(x - t) < BEZIER_EPS) break;
    if (x < t) lo = u;
    else hi = u;
  }
  return bezierAxis(y1, y2, u);
};

const clamp01 = (n: number): number =>
  Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;

/**
 * Stops listos para muestrear: `at` en 0..1, orden creciente y sin repetidos
 * — con varios stops en el mismo `at` gana el ÚLTIMO (cascada de @keyframes),
 * también en el `at` inicial. Devuelve la misma instancia cuando ya está
 * normalizada (nada que alocar por frame); nunca muta la entrada.
 */
/** Sin stops: instancia compartida y CONGELADA (no se puede mutar) para no alocar por frame. */
const EMPTY_STOPS: ReadonlyArray<IOverlayKeyframeStop> = Object.freeze([]);

export const normalizeStops = (
  stops: ReadonlyArray<IOverlayKeyframeStop> | undefined,
): ReadonlyArray<IOverlayKeyframeStop> => {
  if (!stops || stops.length === 0) return EMPTY_STOPS;
  let clean = true;
  for (let i = 0; i < stops.length; i += 1) {
    const at = stops[i].at;
    if (!(at >= 0 && at <= 1) || (i > 0 && at <= stops[i - 1].at)) {
      clean = false;
      break;
    }
  }
  if (clean) return stops;
  const sorted = stops
    .map((s) => ({ at: clamp01(s.at), value: s.value }))
    .sort((a, b) => a.at - b.at);
  const out: IOverlayKeyframeStop[] = [];
  for (const s of sorted) {
    const prev = out[out.length - 1];
    if (prev && prev.at === s.at) out[out.length - 1] = s;
    else out.push(s);
  }
  return out;
};

/**
 * Interpola un track en la fase `p` (0..1): lineal entre los dos stops que la
 * encierran, con `easing` (bezier CSS) aplicado POR SEGMENTO, semántica de
 * animation-timing-function. Fuera del rango de stops mantiene el extremo.
 * Sin stops devuelve `identity`.
 */
export const sampleTrack = (
  stops: ReadonlyArray<IOverlayKeyframeStop> | undefined,
  p: number,
  easing: Easing,
  identity: number,
): number => {
  // Cortocircuito del track ausente ANTES de normalizar: corre por frame y
  // por capa, no tiene que alocar nada.
  if (!stops || stops.length === 0) return identity;
  const s = normalizeStops(stops);
  const first = s[0];
  const last = s[s.length - 1];
  if (p <= first.at) return first.value;
  if (p >= last.at) return last.value;
  for (let i = 0; i < s.length - 1; i += 1) {
    const a = s[i];
    const b = s[i + 1];
    if (p >= b.at) continue;
    const t = cssEase(easing, (p - a.at) / (b.at - a.at));
    return a.value + (b.value - a.value) * t;
  }
  return last.value;
};

/** Estado muestreado de una animación por keyframes (unidades de diseño / grados / 0..1). */
export interface OverlayKeyframeSample {
  x: number;
  y: number;
  rotate: number;
  scaleX: number;
  scaleY: number;
  opacity: number;
}

/**
 * Fase 0..1 del ciclo en `elapsedMs`: antes del delay es 0 (el elemento queda
 * en los valores de `at=0`); loop infinito por módulo; con `alternate` el
 * período es 2×duration y la segunda mitad va espejada.
 */
export const keyframesPhase = (
  anim: Pick<IOverlayElementKeyframes, "durationMs" | "delayMs" | "alternate">,
  elapsedMs: number,
): number => {
  const dur = Math.max(1, anim.durationMs);
  const since = elapsedMs - Math.max(0, anim.delayMs ?? 0);
  if (since <= 0) return 0;
  if (!anim.alternate) return (since % dur) / dur;
  const q = (since % (2 * dur)) / dur; // 0..2
  return q <= 1 ? q : 2 - q;
};

/**
 * Tracks resueltos por propiedad CSS: la ÚNICA fuente de los stops que animan
 * ambos renderers — el @keyframes del DOM los escribe tal cual y Pixi los
 * interpola con `sampleTrack`. Los tracks que comparten propiedad CSS
 * (`translate` = x/y; `scale` = scale·scaleX·scaleY) se fusionan sobre la
 * UNIÓN de sus `at` (más 0 y 1; un track que no llega a un extremo vale la
 * identidad ahí, como en CSS) remuestreando cada uno linealmente, y en scale
 * se guarda el producto por eje. Así el easing por segmento reinicia en los
 * mismos stops que en CSS (y 1→2 · 1→2 da 1→4, no (1+e)²). Propiedad sin
 * tracks → ausente (identidad); x/y y scaleX/scaleY van siempre en pares con
 * los mismos `at`.
 */
export interface IResolvedKeyframeTracks {
  x?: IOverlayKeyframeStop[];
  y?: IOverlayKeyframeStop[];
  rotate?: IOverlayKeyframeStop[];
  scaleX?: IOverlayKeyframeStop[];
  scaleY?: IOverlayKeyframeStop[];
  opacity?: IOverlayKeyframeStop[];
}

const unionAts = (
  lists: ReadonlyArray<ReadonlyArray<IOverlayKeyframeStop>>,
): number[] => {
  const set = new Set<number>([0, 1]);
  for (const list of lists) for (const s of list) set.add(s.at);
  return [...set].sort((a, b) => a - b);
};

// Remuestreo LINEAL (sin easing) en cada `at`: el easing lo pone el segmento.
// Fuera del rango del track (ya normalizado) devuelve la identidad, como CSS
// cuando la propiedad falta en ese extremo: el 0%/100% unido no "estira" el
// primer/último stop.
const lin = (
  stops: ReadonlyArray<IOverlayKeyframeStop>,
  at: number,
  identity: number,
) => {
  if (stops.length === 0) return identity;
  if (at < stops[0].at || at > stops[stops.length - 1].at) return identity;
  return sampleTrack(stops, at, "linear", identity);
};

export const resolveKeyframeTracks = (
  tracks: IOverlayElementKeyframes["tracks"],
): IResolvedKeyframeTracks => {
  const x = normalizeStops(tracks.x);
  const y = normalizeStops(tracks.y);
  const rotate = normalizeStops(tracks.rotate);
  const scale = normalizeStops(tracks.scale);
  const scaleX = normalizeStops(tracks.scaleX);
  const scaleY = normalizeStops(tracks.scaleY);
  const opacity = normalizeStops(tracks.opacity);
  const out: IResolvedKeyframeTracks = {};
  if (x.length || y.length) {
    const ats = unionAts([x, y]);
    out.x = ats.map((at) => ({ at, value: lin(x, at, 0) }));
    out.y = ats.map((at) => ({ at, value: lin(y, at, 0) }));
  }
  if (rotate.length) {
    out.rotate = unionAts([rotate]).map((at) => ({
      at,
      value: lin(rotate, at, 0),
    }));
  }
  if (scale.length || scaleX.length || scaleY.length) {
    const ats = unionAts([scale, scaleX, scaleY]);
    out.scaleX = ats.map((at) => ({
      at,
      value: lin(scale, at, 1) * lin(scaleX, at, 1),
    }));
    out.scaleY = ats.map((at) => ({
      at,
      value: lin(scale, at, 1) * lin(scaleY, at, 1),
    }));
  }
  if (opacity.length) {
    out.opacity = unionAts([opacity]).map((at) => ({
      at,
      value: lin(opacity, at, 1),
    }));
  }
  return out;
};

const resolvedCache = new WeakMap<
  IOverlayElementKeyframes,
  IResolvedKeyframeTracks
>();

/** `resolveKeyframeTracks` memoizado por objeto de animación: se resuelve una vez, no por frame. */
export const resolvedKeyframeTracks = (
  anim: IOverlayElementKeyframes,
): IResolvedKeyframeTracks => {
  const cached = resolvedCache.get(anim);
  if (cached) return cached;
  const resolved = resolveKeyframeTracks(anim.tracks);
  resolvedCache.set(anim, resolved);
  return resolved;
};

/**
 * Muestrea los tracks resueltos en `elapsedMs`. Propiedad ausente = identidad
 * (x=y=0, rotate=0, scale=1, opacity=1). Default de easing = "ease" (el de
 * CSS cuando la animación no lo declara).
 */
export const sampleKeyframes = (
  anim: IOverlayElementKeyframes,
  elapsedMs: number,
  resolved: IResolvedKeyframeTracks = resolvedKeyframeTracks(anim),
): OverlayKeyframeSample => {
  const p = keyframesPhase(anim, elapsedMs);
  const easing: Easing = anim.easing ?? "ease";
  return {
    x: sampleTrack(resolved.x, p, easing, 0),
    y: sampleTrack(resolved.y, p, easing, 0),
    rotate: sampleTrack(resolved.rotate, p, easing, 0),
    scaleX: sampleTrack(resolved.scaleX, p, easing, 1),
    scaleY: sampleTrack(resolved.scaleY, p, easing, 1),
    opacity: sampleTrack(resolved.opacity, p, easing, 1),
  };
};

/**
 * Aplica el estado de la animación al `container` según el tiempo transcurrido.
 * Idempotente: para entry-anims completadas deja el estado final (identidad);
 * para `pulse` (loop) y `keyframes` oscila / cicla indefinidamente.
 */
export const applyOverlayAnimation = (
  handle: OverlayAnimHandle,
  elapsedMs: number,
): boolean => {
  const { container: c, animation: a, w, h } = handle;
  if (c.destroyed) return true;

  // Keyframes importados (chrome de escena en capas): CustomOverlayRenderer ya
  // dejó el pivot del fx en `origin`, así position = pivot + translate y la
  // rotación/escala giran alrededor de él (= transform-origin + translate →
  // rotate → scale del DOM). Infinita: nunca devuelve true.
  if (a.kind === "keyframes") {
    const s = sampleKeyframes(a, elapsedMs);
    c.alpha = s.opacity;
    c.position.set(c.pivot.x + s.x, c.pivot.y + s.y);
    c.rotation = (s.rotate * Math.PI) / 180;
    c.scale.set(s.scaleX, s.scaleY);
    return false;
  }

  const baseX = c.pivot.x;
  const baseY = c.pivot.y;
  const delay = Math.max(0, a.delayMs ?? 0);
  const dur = Math.max(1, a.durationMs);
  const since = elapsedMs - delay;

  // progress 0..1. loop → cicla (como animation-iteration:infinite del DOM);
  // sin loop → clamp a 1 (queda en el estado final). Aplica a TODOS los kinds.
  const t =
    since <= 0 ? 0 : a.loop ? (since % dur) / dur : Math.min(1, since / dur);

  // Una animación finita termina al llegar a su estado final: el ticker la
  // descarta (devuelve true) después de aplicarla por última vez.
  const done = !a.loop && since >= dur;

  // pulse: oscilación triangular 1 → 1.05 → 1 (un ciclo, o repetido si loop).
  if (a.kind === "pulse") {
    const s = 1 + 0.05 * (1 - Math.abs(2 * t - 1));
    c.alpha = 1;
    c.scale.set(s);
    c.position.set(baseX, baseY);
    return done;
  }

  const p = ease(a.easing ?? "ease-out", t);

  let alpha = 1;
  let sx = 1;
  let sy = 1;
  let dx = 0;
  let dy = 0;

  switch (a.kind) {
    case "fade-in":
      alpha = p;
      break;
    case "scale-in":
      alpha = p;
      sx = sy = 0.5 + 0.5 * p;
      break;
    case "slide-in-top":
      alpha = p;
      dy = -h * (1 - p);
      break;
    case "slide-in-bottom":
      alpha = p;
      dy = h * (1 - p);
      break;
    case "slide-in-left":
      alpha = p;
      dx = -w * (1 - p);
      break;
    case "slide-in-right":
      alpha = p;
      dx = w * (1 - p);
      break;
    // "pulse" se maneja antes (return temprano).
  }

  c.alpha = alpha;
  c.scale.set(sx, sy);
  c.position.set(baseX + dx, baseY + dy);
  return done;
};
